import React, { Fragment, useState, useEffect } from 'react';
import { Breadcrumbs, H3, H5 } from '../../../AbstractElements';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Button, Input, FormGroup, Label, Modal, ModalHeader, ModalBody, ModalFooter, Badge } from 'reactstrap';
import { Tag, Edit, Trash2, Plus, Search, ChevronUp, ChevronDown, ToggleLeft, ToggleRight } from 'react-feather';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../../Config/api';

const AllCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentCategory, setCurrentCategory] = useState({
        id: null,
        name: '',
        description: '',
        is_active: true
    });

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/categories/all`);
            if (!response.ok) {
                throw new Error('Failed to fetch categories');
            }
            const data = await response.json();
            setCategories(data);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = () => {
        setEditMode(false);
        setCurrentCategory({
            id: null,
            name: '',
            description: '',
            is_active: true
        });
        setModalOpen(true);
    };

    const handleEditCategory = (category) => {
        setEditMode(true);
        setCurrentCategory({
            id: category.id,
            name: category.name,
            description: category.description || '',
            is_active: Boolean(category.is_active)
        });
        setModalOpen(true);
    };

    const handleDeleteCategory = async (categoryId) => {
        if (window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}`, {
                    method: 'DELETE'
                });
                if (response.ok) {
                    toast.success('Category deleted successfully');
                    fetchCategories();
                } else {
                    const error = await response.json();
                    toast.error(error.error || 'Failed to delete category');
                }
            } catch (error) {
                console.error('Error deleting category:', error);
                toast.error('Error deleting category');
            }
        }
    };

    const handleToggleActive = async (categoryId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/categories/${categoryId}/toggle`, {
                method: 'PATCH'
            });
            if (response.ok) {
                const result = await response.json();
                toast.success(`Category ${result.is_active ? 'activated' : 'deactivated'} successfully`);
                fetchCategories();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to toggle category status');
            }
        } catch (error) {
            console.error('Error toggling category status:', error);
            toast.error('Error toggling category status');
        }
    };

    const handleSaveCategory = async () => {
        if (!currentCategory.name.trim()) {
            toast.error('Category name is required');
            return;
        }

        try {
            const url = editMode
                ? `${API_BASE_URL}/api/categories/${currentCategory.id}`
                : `${API_BASE_URL}/api/categories`;

            const method = editMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: currentCategory.name,
                    description: currentCategory.description,
                    is_active: currentCategory.is_active
                })
            });

            if (response.ok) {
                toast.success(`Category ${editMode ? 'updated' : 'created'} successfully`);
                setModalOpen(false);
                fetchCategories();
            } else {
                const error = await response.json();
                toast.error(error.error || `Failed to ${editMode ? 'update' : 'create'} category`);
            }
        } catch (error) {
            console.error(`Error ${editMode ? 'updating' : 'creating'} category:`, error);
            toast.error(`Error ${editMode ? 'updating' : 'creating'} category`);
        }
    };

    const toggleSortOrder = () => {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const getFilteredAndSortedCategories = () => {
        let filtered = categories;

        if (searchTerm) {
            filtered = categories.filter(category => {
                const searchLower = searchTerm.toLowerCase();
                return (
                    (category.name && category.name.toLowerCase().includes(searchLower)) ||
                    (category.description && category.description.toLowerCase().includes(searchLower))
                );
            });
        }

        const sorted = [...filtered].sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();

            if (sortOrder === 'asc') {
                return nameA.localeCompare(nameB);
            } else {
                return nameB.localeCompare(nameA);
            }
        });

        return sorted;
    };

    const filteredCategories = getFilteredAndSortedCategories();

    if (loading) {
        return (
            <Container fluid={true}>
                <div className="text-center mt-5">
                    <p>Loading categories...</p>
                </div>
            </Container>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs parent="Settings" title="Categories" mainTitle="Manage Categories" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <Row className="align-items-center">
                                    <Col md="6">
                                        <H5>Product Categories</H5>
                                    </Col>
                                    <Col md="6" className="text-end">
                                        <Button color="primary" onClick={handleAddCategory}>
                                            <Plus size={16} className="me-1" />
                                            Add Category
                                        </Button>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <Row className="mb-3">
                                    <Col md="6">
                                        <FormGroup className="mb-0">
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <Search size={16} />
                                                </span>
                                                <Input
                                                    type="text"
                                                    placeholder="Search categories..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md="6" className="text-end">
                                        <p className="mb-0 mt-2">Total Categories: <strong>{filteredCategories.length}</strong></p>
                                    </Col>
                                </Row>

                                <div className="table-responsive">
                                    <Table hover>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th onClick={toggleSortOrder} style={{ cursor: 'pointer' }}>
                                                    Category Name {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                </th>
                                                <th>Description</th>
                                                <th>Status</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCategories.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center">
                                                        No categories found
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredCategories.map((category, index) => (
                                                    <tr key={category.id}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <strong>{category.name}</strong>
                                                        </td>
                                                        <td>{category.description || '-'}</td>
                                                        <td>
                                                            <Badge color={category.is_active ? 'success' : 'secondary'}>
                                                                {category.is_active ? 'Active' : 'Inactive'}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Button
                                                                color={category.is_active ? 'warning' : 'success'}
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleToggleActive(category.id)}
                                                                title={category.is_active ? 'Deactivate' : 'Activate'}
                                                            >
                                                                {category.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                                            </Button>
                                                            <Button
                                                                color="info"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleEditCategory(category)}
                                                                title="Edit"
                                                            >
                                                                <Edit size={16} />
                                                            </Button>
                                                            <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => handleDeleteCategory(category.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Add/Edit Category Modal */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
                <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    {editMode ? 'Edit Category' : 'Add New Category'}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="categoryName">Category Name <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            id="categoryName"
                            placeholder="Enter category name"
                            value={currentCategory.name}
                            onChange={(e) => setCurrentCategory({ ...currentCategory, name: e.target.value })}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label for="categoryDescription">Description</Label>
                        <Input
                            type="textarea"
                            id="categoryDescription"
                            placeholder="Enter category description (optional)"
                            value={currentCategory.description}
                            onChange={(e) => setCurrentCategory({ ...currentCategory, description: e.target.value })}
                            rows="3"
                        />
                    </FormGroup>
                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={currentCategory.is_active}
                                onChange={(e) => setCurrentCategory({ ...currentCategory, is_active: e.target.checked })}
                            />
                            {' '}Active
                        </Label>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleSaveCategory}>
                        {editMode ? 'Update' : 'Create'}
                    </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default AllCategories;
